import React, { useCallback, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import moment from "moment-timezone";

import { getMapsAtLocation, MapAtLocationSummary } from "api/Locations";
import { attachMapToRecord, AttachMapWarning } from "api/MulchMap";

import styles from "./styles.module.scss";

interface Props {
  locationId: string;
  recordType: "quote_request" | "quote" | "project" | "invoice" | "purchase_order";
  recordId: string;
  /**
   * The current version-id (mulchMapId column) already attached to the
   * record — used to highlight the matching row and offer a "Detach" action.
   */
  currentMulchMapId?: string | null;
  onClose: () => void;
  /**
   * Called after a successful attach/detach with the new value that lives
   * on the record. Parent should refetch or otherwise reflect the change.
   */
  onAttached: (newMulchMapId: string | null) => void;
}

/**
 * Session 20 Commit 4 — pick a Map at the record's Location and attach it
 * (or detach the current one). Reuses the same "one row per mapGroup"
 * shape the ExistingMapsChooser uses at the calculator's ad-hoc entry
 * point, so the mental model stays consistent.
 *
 * Attaches the CURRENT version of the chosen Map. If a downstream record
 * needs to be pinned to a specific older version, the version history
 * modal can restore that version to current first (Commit 2 makes this
 * scoped-per-mapGroup), then this modal attaches it.
 *
 * Phase B: when the picked Map belongs to a different client profile than
 * the record, the backend returns { success: false, requiresConfirmation }
 * with a warning shape describing the mismatch. This modal surfaces that
 * as an inline banner with an "Attach anyway" affordance; confirming
 * re-calls the endpoint with skipProfileWarning=true.
 */
const AttachMapModal: React.FC<Props> = ({
  locationId,
  recordType,
  recordId,
  currentMulchMapId,
  onClose,
  onAttached,
}) => {
  const [maps, setMaps] = useState<MapAtLocationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Cross-profile confirmation state. When set, the modal shows a banner
  // and the row that triggered the warning holds the pending attach until
  // the user confirms or dismisses.
  const [pending, setPending] = useState<{
    map: MapAtLocationSummary;
    warning: AttachMapWarning;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getMapsAtLocation(locationId).then((res) => {
      if (cancelled) return;
      setMaps(Array.isArray(res) ? res : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  /**
   * Attach flow. Two paths:
   *   - First call: no skipProfileWarning. Backend may return
   *     requiresConfirmation → we stash the pending map + warning and
   *     render the confirmation banner instead of closing.
   *   - Confirm call: skipProfileWarning=true. Backend attaches
   *     unconditionally and echoes the warning for logging.
   */
  const doAttach = useCallback(
    async (m: MapAtLocationSummary, skipProfileWarning: boolean) => {
      setBusyId(m.mapGroupId);
      setError(null);
      const res = await attachMapToRecord({
        recordType,
        recordId,
        mulchMapId: m.currentMapId,
        ...(skipProfileWarning ? { skipProfileWarning: true } : {}),
      });
      setBusyId(null);

      // Gate response: backend wants confirmation before proceeding.
      if (
        res.success === false &&
        "requiresConfirmation" in res &&
        res.requiresConfirmation
      ) {
        setPending({ map: m, warning: res.warning });
        return;
      }

      if (res.success) {
        onAttached(m.currentMapId);
        onClose();
        return;
      }

      setError(
        "error" in res && res.error
          ? res.error
          : "Attach failed.",
      );
    },
    [recordType, recordId, onAttached, onClose],
  );

  const handleAttach = useCallback(
    (m: MapAtLocationSummary) => doAttach(m, false),
    [doAttach],
  );

  const handleConfirmCrossProfile = useCallback(() => {
    if (!pending) return;
    const m = pending.map;
    setPending(null);
    doAttach(m, true);
  }, [pending, doAttach]);

  const handleDismissPending = useCallback(() => {
    setPending(null);
  }, []);

  const handleDetach = useCallback(async () => {
    setBusyId("__detach__");
    setError(null);
    const res = await attachMapToRecord({
      recordType,
      recordId,
      mulchMapId: null,
    });
    setBusyId(null);
    if (res.success) {
      onAttached(null);
      onClose();
    } else {
      setError(
        "error" in res && res.error ? res.error : "Detach failed.",
      );
    }
  }, [recordType, recordId, onAttached, onClose]);

  return (
    <Modal isOpen={true} toggle={onClose} size="md" centered>
      <ModalHeader toggle={onClose}>Attach site map</ModalHeader>
      <ModalBody>
        {pending ? (
          <div className={styles.warningBanner}>
            <div className={styles.warningTitle}>
              Cross-client attach — confirm?
            </div>
            <p className={styles.warningBody}>
              <strong>{pending.map.name || "Untitled map"}</strong> is
              assigned to
              {pending.warning.mapClientName ? (
                <> <strong>{pending.warning.mapClientName}</strong>.</>
              ) : (
                <> a different client.</>
              )}{" "}
              This record is under
              {pending.warning.recordClientName ? (
                <> <strong>{pending.warning.recordClientName}</strong>.</>
              ) : (
                <> a different client.</>
              )}{" "}
              Attach anyway?
            </p>
            <p className={styles.warningHint}>
              Reps sometimes want the polygons and don&apos;t care about the
              client attribution — this is fine. If it wasn&apos;t intended,
              cancel and pick a map assigned to this record&apos;s client.
            </p>
            <div className={styles.warningActions}>
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={handleDismissPending}
                styles={{ margin: "0" }}
                keepText
              >
                Cancel
              </BasicButton>
              <BasicButton
                type="button"
                scheme="primary"
                onClick={handleConfirmCrossProfile}
                disabled={!!busyId}
                styles={{ margin: "0" }}
                keepText
              >
                {busyId === pending.map.mapGroupId
                  ? "Attaching\u2026"
                  : "Attach anyway"}
              </BasicButton>
            </div>
          </div>
        ) : (
          <p className={styles.blurb}>
            Pick a map to attach to this record. Attaching preserves the
            exact current version — later edits to the map create a new
            version and this record stays pinned to the one you picked.
          </p>
        )}

        {loading && <p className={styles.status}>Loading maps…</p>}

        {!loading && maps.length === 0 && (
          <div className={styles.emptyState}>
            No maps saved at this site yet. Draw one from the Mulch Calculator
            first, then come back here to attach it.
          </div>
        )}

        {!loading && maps.length > 0 && !pending && (
          <div className={styles.mapList}>
            {maps.map((m) => {
              const isCurrent = currentMulchMapId === m.currentMapId;
              return (
                <div
                  key={m.mapGroupId}
                  className={`${styles.mapRow} ${
                    isCurrent ? styles.mapRowSelected : ""
                  }`}
                >
                  <div className={styles.mapRowMain}>
                    <div className={styles.mapRowHeader}>
                      <strong>{m.name || "Untitled map"}</strong>
                      {isCurrent && (
                        <span className={styles.attachedBadge}>Attached</span>
                      )}
                      {m.clientName && (
                        <span className={styles.clientBadge}>
                          {m.clientName}
                        </span>
                      )}
                    </div>
                    <div className={styles.mapRowMeta}>
                      v{m.version} · {m.zoneCount} zone
                      {m.zoneCount === 1 ? "" : "s"} ·{" "}
                      {m.totalCuYd.toFixed(1)} cu yd · edited{" "}
                      {moment(m.updatedAt).fromNow()}
                    </div>
                  </div>
                  <BasicButton
                    type="button"
                    scheme={isCurrent ? "secondary" : "primary"}
                    outline={isCurrent}
                    onClick={() => handleAttach(m)}
                    disabled={!!busyId || isCurrent}
                    styles={{ margin: "0" }}
                    keepText
                  >
                    {busyId === m.mapGroupId
                      ? "Attaching\u2026"
                      : isCurrent
                        ? "Attached"
                        : "Attach"}
                  </BasicButton>
                </div>
              );
            })}
          </div>
        )}

        {error && <p className={styles.errorLine}>{error}</p>}
      </ModalBody>
      <ModalFooter>
        {currentMulchMapId && !pending && (
          <BasicButton
            type="button"
            scheme="dangerSecondary"
            onClick={handleDetach}
            disabled={busyId === "__detach__"}
            styles={{ margin: "0 auto 0 0" }}
            keepText
          >
            {busyId === "__detach__" ? "Detaching\u2026" : "Detach current map"}
          </BasicButton>
        )}
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onClose}
          styles={{ margin: "0" }}
        >
          Cancel
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default AttachMapModal;
